// client/src/components/audience/MessageReport.jsx
//
// /:tenant/audience/messages/:sendId — how one audience message did:
// delivered / opened / clicked / converted / revenue, with the email and push
// legs broken out. Conversions and revenue are the ones credited to this
// message's tracking links (last link touch within 45 days, Phase 1
// attribution). Refreshes every 30 s while the message is still going out.
// Growth Phase 5, feature_key "audience_messaging".

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { cancelSend, errorText, getReport } from '../../services/audienceApi';
import { formatCount, formatMoney } from '../analytics/attributionFormat';
import { channelLabel, statusLabel } from './AudiencePage';
import { RefreshButton } from '../common/IconButton';
import '../analytics/PerformancePanel.css';
import './Audience.css';

const LIVE = ['scheduled', 'preparing', 'sending'];
const REFRESH_MS = 30_000;

/** "12 (40%)" — share of `of`, or just the count. */
export function withRate(n, of) {
  const v = Number(n) || 0;
  const d = Number(of) || 0;
  return d > 0 ? `${formatCount(v)} (${Math.round((v / d) * 100)}%)` : formatCount(v);
}

export default function MessageReport({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const tenant = Number(tenantProp || params.tenant);
  const sendId = Number(params.sendId);

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await getReport(tenant, sendId);
      setData(res.data);
      setError('');
    } catch (err) {
      setError(errorText(err, t('audience.report.loadError', 'Could not load the report.')));
    }
  }, [tenant, sendId, t]);

  useEffect(() => { load(); }, [load]);
  const status = data?.send?.status;
  useEffect(() => {
    if (!LIVE.includes(status)) return undefined;
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [status, load]);

  const cancel = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('audience.report.cancelConfirm', 'Stop this message? People not reached yet will not get it.'))) return;
    try {
      await cancelSend(tenant, sendId);
      setNotice({ kind: 'ok', text: t('audience.report.cancelled', 'Stopped.') });
      load();
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err, t('audience.report.cancelError', 'Could not stop it.')) });
    }
  };

  if (error) return <div className="au-page"><ErrorState message={error} onRetry={load} /></div>;
  if (!data) return <div className="au-page"><LoadingState /></div>;

  const { send, totals, email, push, conversionsByKind } = data;
  // performanceReport: { [kind]: { count, value } }
  const kinds = Object.entries(conversionsByKind || {}).filter(([, v]) => Number(v?.count) > 0);

  return (
    <div className="au-page">
      <section className="surface">
        <div className="au-head">
          <h2>{send.name}</h2>
          <span className="lm-badge au-status">{statusLabel(t, send.status)}</span>
        </div>
        <dl className="au-kv">
          <dt>{t('audience.sends.col.channel', 'Channel')}</dt><dd>{channelLabel(t, send.channel)}</dd>
          <dt>{t('audience.sends.col.segment', 'Segment')}</dt><dd>{send.segmentName || '—'}</dd>
          {send.scheduledAt && (<><dt>{t('audience.report.scheduled', 'Scheduled for')}</dt><dd>{send.scheduledAt}</dd></>)}
          {send.completedAt && (<><dt>{t('audience.report.finished', 'Finished')}</dt><dd>{send.completedAt}</dd></>)}
        </dl>
        {send.lastError && <div className="note warn">{send.lastError}</div>}
        <div className="au-actions" style={{ marginTop: 8 }}>
          {['draft', 'scheduled'].includes(send.status) && (
            <button type="button" className="lm-btn secondary" onClick={() => navigate(`/${tenant}/audience/messages/${sendId}/edit`)}>
              {t('audience.edit', 'Edit')}
            </button>
          )}
          {LIVE.includes(send.status) && (
            <button type="button" className="lm-btn secondary" onClick={cancel}>{t('audience.report.cancel', 'Stop sending')}</button>
          )}
          <RefreshButton onClick={load} label={t('audience.report.refresh', 'Refresh')} />
        </div>
        {notice && <div className={`note ${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>{notice.text}</div>}
      </section>

      <section className="surface">
        <div className="perf-kpis">
          <Kpi label={t('audience.report.delivered', 'Delivered')} value={formatCount(totals.delivered)} />
          <Kpi label={t('audience.report.opened', 'Opened (email)')} value={withRate(totals.opened, email?.sent)} />
          <Kpi label={t('audience.report.clicked', 'Clicked')} value={formatCount(totals.clicked)} />
          <Kpi label={t('audience.report.converted', 'Converted')} value={formatCount(totals.converted)} />
          <Kpi label={t('audience.report.revenue', 'Revenue')} value={formatMoney(totals.revenue)} />
        </div>
        <p className="surface-muted perf-small">
          {t('audience.report.attributionNote', 'Converted and revenue count purchases, entries and sign-ups by people whose last click on one of your tracked links before buying was in this message (45-day window).')}
        </p>
      </section>

      {email && (
        <section className="surface">
          <h4>{t('audience.channel.email', 'Email')}</h4>
          <div className="perf-table-wrap">
            <table className="perf-table">
              <tbody>
                <Row label={t('audience.report.recipients', 'Recipients')} value={formatCount(email.total)} />
                <Row label={t('audience.report.sent', 'Sent')} value={formatCount(email.sent)} />
                <Row label={t('audience.report.opened', 'Opened (email)')} value={withRate(email.opened, email.sent)} />
                <Row label={t('audience.report.clickedEmail', 'Clicked')} value={withRate(email.clicked, email.sent)} />
                <Row label={t('audience.report.unsubscribed', 'Unsubscribed')} value={formatCount(email.unsubscribed)} />
                <Row label={t('audience.report.skipped', 'Skipped (unsubscribed or daily limit)')} value={formatCount(email.skipped)} />
                <Row label={t('audience.report.failed', 'Failed')} value={formatCount(email.failed)} />
              </tbody>
            </table>
          </div>
        </section>
      )}

      {(send.channel === 'push' || send.channel === 'both') && (
        <section className="surface">
          <h4>{t('audience.channel.push', 'Push')}</h4>
          <div className="perf-table-wrap">
            <table className="perf-table">
              <tbody>
                <Row label={t('audience.report.devicesPeople', 'People with notifications on')} value={formatCount(push.total)} />
                <Row label={t('audience.report.sent', 'Sent')} value={formatCount(push.sent)} />
                <Row label={t('audience.report.waiting', 'Waiting')} value={formatCount(push.pending)} />
                <Row label={t('audience.report.clickedPush', 'Link clicks')} value={formatCount(push.clicked)} />
                <Row label={t('audience.report.skippedPush', 'Skipped (daily limit or notifications off)')} value={formatCount(push.skipped)} />
                <Row label={t('audience.report.failed', 'Failed')} value={formatCount(push.failed)} />
              </tbody>
            </table>
          </div>
        </section>
      )}

      {kinds.length > 0 && (
        <section className="surface">
          <h4>{t('audience.report.byKind', 'Conversions by type')}</h4>
          <div className="perf-table-wrap">
            <table className="perf-table">
              <tbody>
                {kinds.map(([kind, v]) => (
                  <Row key={kind} label={t(`performance.kinds.${kind}`, kind.replace(/_/g, ' '))} value={formatCount(v.count)} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(`/${tenant}/audience`) },
          { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="perf-kpi">
      <div className="perf-kpi-value">{value}</div>
      <div className="perf-kpi-label">{label}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <tr>
      <th scope="row">{label}</th>
      <td className="perf-num">{value}</td>
    </tr>
  );
}
